import { ActionReducerMapBuilder } from "@reduxjs/toolkit";
import type { WhatsAppState } from "../../Slice/Dashboard/WhatsAppSlice";
import {
  getWhatsAppAccounts,
  getWalletBalance,
  getWhatsAppConversations,
  getWhatsAppMessages,
  sendWhatsAppMessage,
  getWalletTransactions,
  getWalletPlans,
  getWhatsAppCampaigns,
  getWhatsAppCampaignDetail,
  createWhatsAppCampaign,
  launchWhatsAppCampaign,
  getStudioAlbums,
  getRedeemUsers,
  getCampaignAudience,
  getCampaignWalletEstimate,
  getWhatsAppTemplates,
  getWhatsAppTemplateDetail,
  createWhatsAppTemplate,
  syncWhatsAppTemplate,
  deleteWhatsAppTemplate,
  createRechargeOrder,
  verifyRecharge,
  cancelRechargeOrder,
  getRechargeOrders,
} from "../../ApiCalls/Dashboard/WhatsAppAPI";
import { showErrorToast, showSuccessToast } from "../../../components/atoms/Utlis/Toast";
import type {
  WhatsAppMessageItem,
  WhatsAppConversationsResponse,
  WhatsAppMessagesResponse,
  WhatsAppSendMessageResponse,
  WalletTransactionsResponse,
} from "../../../Data/whatsapp.dto";

const isSameApproximateTime = (t1: string, t2: string) => {
  try {
    const diff = Math.abs(new Date(t1).getTime() - new Date(t2).getTime());
    return diff < 10000; // 10 seconds
  } catch (e) {
    return false;
  }
};

const mergeMessages = (
  incoming: WhatsAppMessageItem[],
  existing: WhatsAppMessageItem[],
): WhatsAppMessageItem[] => {
  const all = [...incoming, ...existing];
  const seenIds = new Set<string>();
  const seenProviderIds = new Set<string>();
  const unique: WhatsAppMessageItem[] = [];

  for (const message of all) {
    if (seenIds.has(message.id)) continue;
    if (message.provider_message_id && seenProviderIds.has(message.provider_message_id)) {
      continue;
    }

    const isDuplicate = unique.some(u => 
      u.customer_number === message.customer_number &&
      u.text === message.text &&
      isSameApproximateTime(u.created_at, message.created_at)
    );
    if (isDuplicate) continue;

    seenIds.add(message.id);
    if (message.provider_message_id) {
      seenProviderIds.add(message.provider_message_id);
    }
    unique.push(message);
  }

  return unique.sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
};

export const WhatsAppReducer = (builder: ActionReducerMapBuilder<WhatsAppState>) => {
  builder
    // ── WhatsApp Account ────────────────────────────────────────────
    .addCase(getWhatsAppAccounts.pending, (state) => {
      state.accountLoading = true;
      state.isError = false;
      state.error = {};
    })
    .addCase(getWhatsAppAccounts.fulfilled, (state, action) => {
      state.accountLoading = false;
      state.error = { ...state.error, account: "" };
      state.whatsAppAccount = action.payload ?? null;
    })
    .addCase(getWhatsAppAccounts.rejected, (state, action) => {
      state.accountLoading = false;
      state.isError = true;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to load WhatsApp account.";
      state.error = { account: msg };
      showErrorToast(msg);
    })

    // ── Wallet Balance ──────────────────────────────────────────────
    .addCase(getWalletBalance.pending, (state) => {
      state.walletLoading = true;
      state.error = { ...state.error, wallet: "" };
    })
    .addCase(getWalletBalance.fulfilled, (state, action) => {
      state.walletLoading = false;
      state.error = { ...state.error, wallet: "" };
      state.walletBalance = action.payload ?? null;
    })
    .addCase(getWalletBalance.rejected, (state, action) => {
      state.walletLoading = false;
      state.isError = true;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to load wallet balance.";
      state.error = { ...state.error, wallet: msg };
      showErrorToast(msg);
    })

    // ── Conversations (paginated) ───────────────────────────────────
    .addCase(getWhatsAppConversations.pending, (state) => {
      state.conversationsLoading = true;
      state.error = { ...state.error, conversations: "" };
    })
    .addCase(getWhatsAppConversations.fulfilled, (state, action) => {
      state.conversationsLoading = false;
      state.error = { ...state.error, conversations: "" };
      const data = action.payload as WhatsAppConversationsResponse;
      if (data.page === 1) {
        state.conversations = data.results ?? [];
      } else {
        state.conversations = [...state.conversations, ...(data.results ?? [])];
      }
      state.conversationsPage = data.page;
      state.conversationsHasMore = data.page * data.page_size < data.count;
    })
    .addCase(getWhatsAppConversations.rejected, (state, action) => {
      state.conversationsLoading = false;
      state.isError = true;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to load conversations.";
      state.error = { ...state.error, conversations: msg };
      showErrorToast(msg);
    })

    // ── Messages (paginated) ────────────────────────────────────────
    .addCase(getWhatsAppMessages.pending, (state) => {
      state.messagesLoading = true;
      state.error = { ...state.error, messages: "" };
    })
    .addCase(getWhatsAppMessages.fulfilled, (state, action) => {
      state.messagesLoading = false;
      state.error = { ...state.error, messages: "" };
      const data = action.payload as WhatsAppMessagesResponse;
      if (data.page === 1) {
        state.messages = mergeMessages(data.results ?? [], state.messages);
      } else {
        state.messages = mergeMessages([...state.messages, ...(data.results ?? [])], []);
      }
      state.messagesPage = data.page;
      state.messagesHasMore = data.page * data.page_size < data.count;
    })
    .addCase(getWhatsAppMessages.rejected, (state, action) => {
      state.messagesLoading = false;
      state.isError = true;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to load messages.";
      state.error = { ...state.error, messages: msg };
      showErrorToast(msg);
    })

    // ── Send Message ────────────────────────────────────────────────
    .addCase(sendWhatsAppMessage.pending, (state) => {
      state.sendingMessage = true;
      state.error = { ...state.error, sendMessage: "" };
    })
    .addCase(sendWhatsAppMessage.fulfilled, (state, action) => {
      state.sendingMessage = false;
      const { response, payload, mockMessage } = action.payload as {
        response: WhatsAppSendMessageResponse;
        payload: { to: string; text: string };
        mockMessage?: WhatsAppMessageItem;
      };
      if (response.ok && response.sent_count > 0) {
        const sent = response.sent?.[0];
        const providerRequestId = sent?.msg91?.request_id ?? "";
        const createdAt = new Date().toISOString();
        const newMessage: WhatsAppMessageItem = mockMessage ?? {
          id: providerRequestId ? `local_${providerRequestId}` : `local_${Date.now()}`,
          direction: "outbound",
          integrated_number: state.activeConversation?.integrated_number ?? "",
          customer_number: payload.to,
          text: payload.text,
          provider_message_id: providerRequestId,
          message_mode: "live",
          pricing_category: sent?.pricing_category ?? "service",
          is_billable: sent?.billable ?? true,
          is_charge_applied: sent?.charge_applied ?? false,
          created_at: createdAt,
        };
        state.messages = mergeMessages([...state.messages, newMessage], []);

        if (state.activeConversation) {
          state.activeConversation.pricing_category = newMessage.pricing_category;
          state.activeConversation.charge_applied = newMessage.is_charge_applied;
          state.activeConversation.charge_applied_at = newMessage.is_charge_applied
            ? createdAt
            : state.activeConversation.charge_applied_at;
        }

        if (state.walletBalance?.data) {
          if (sent?.wallet_balance_after) {
            state.walletBalance.data.balance = sent.wallet_balance_after;
          }
          if (sent?.wallet_charge_inr && parseFloat(sent.wallet_charge_inr) > 0) {
            const currentDebited = parseFloat(state.walletBalance.data.total_debited || "0");
            const charge = parseFloat(sent.wallet_charge_inr);
            state.walletBalance.data.total_debited = (currentDebited + charge).toFixed(2);
          }
        }
      }
    })
    .addCase(sendWhatsAppMessage.rejected, (state, action) => {
      state.sendingMessage = false;
      state.isError = true;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to send message.";
      state.error = { ...state.error, sendMessage: msg };
      showErrorToast(msg);
    })

    // ── Wallet Transactions ─────────────────────────────────────────
    .addCase(getWalletTransactions.pending, (state) => {
      state.loading = true;
      state.error = { ...state.error, transactions: "" };
    })
    .addCase(getWalletTransactions.fulfilled, (state, action) => {
      state.loading = false;
      const data = action.payload as WalletTransactionsResponse;
      if (data.success && data.data) {
        const incoming = data.data.results ?? [];
        if (data.data.current_page === 1) {
          state.walletTransactions = incoming;
        } else {
          state.walletTransactions = [...state.walletTransactions, ...incoming];
        }
        state.walletTransactionsPage = data.data.current_page;
        state.walletTransactionsTotalPages = data.data.total_pages;
      }
    })
    .addCase(getWalletTransactions.rejected, (state, action) => {
      state.loading = false;
      state.isError = true;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to load transactions.";
      state.error = { ...state.error, transactions: msg };
      showErrorToast(msg);
    })

    // ── Wallet Plans ────────────────────────────────────────────────
    .addCase(getWalletPlans.fulfilled, (state, action) => {
      const data = action.payload;
      if (data?.success && data.data?.results) {
        state.walletPlans = data.data.results;
      }
    })
    .addCase(getWalletPlans.rejected, (state, action) => {
      state.isError = true;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to load plans.";
      state.error = { ...state.error, plans: msg };
      showErrorToast(msg);
    })

    // ── Campaigns ───────────────────────────────────────────────────
    .addCase(getWhatsAppCampaigns.pending, (state) => {
      state.loading = true;
      state.error = { ...state.error, campaigns: "" };
    })
    .addCase(getWhatsAppCampaigns.fulfilled, (state, action) => {
      state.loading = false;
      state.error = { ...state.error, campaigns: "" };
      state.campaigns = action.payload ?? [];
    })
    .addCase(getWhatsAppCampaigns.rejected, (state, action) => {
      state.loading = false;
      state.isError = true;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to load campaigns.";
      state.error = { ...state.error, campaigns: msg };
      showErrorToast(msg);
    })

    .addCase(getWhatsAppCampaignDetail.pending, (state) => {
      state.activeCampaignLoading = true;
    })
    .addCase(getWhatsAppCampaignDetail.fulfilled, (state, action) => {
      state.activeCampaignLoading = false;
      state.activeCampaignDetail = action.payload ?? null;
      if (action.payload) {
        const idx = state.campaigns.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) {
          state.campaigns[idx] = action.payload;
        }
      }
    })
    .addCase(getWhatsAppCampaignDetail.rejected, (state, action) => {
      state.activeCampaignLoading = false;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to load campaign details.";
      showErrorToast(msg);
    })

    .addCase(createWhatsAppCampaign.pending, (state) => {
      state.loading = true;
    })
    .addCase(createWhatsAppCampaign.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.campaigns = [action.payload, ...state.campaigns];
        if (action.payload.status === "completed" || action.payload.status === "sending") {
          showSuccessToast("Campaign broadcast launched successfully!");
        } else {
          showSuccessToast("Campaign draft saved successfully.");
        }
      }
    })
    .addCase(createWhatsAppCampaign.rejected, (state, action) => {
      state.loading = false;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to create campaign.";
      showErrorToast(msg);
    })

    .addCase(launchWhatsAppCampaign.pending, (state, action) => {
      state.launchingCampaignId = action.meta.arg;
    })
    .addCase(launchWhatsAppCampaign.fulfilled, (state, action) => {
      state.launchingCampaignId = null;
      if (action.payload) {
        const idx = state.campaigns.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) {
          state.campaigns[idx] = action.payload;
        }
        if (state.activeCampaignDetail?.id === action.payload.id) {
          state.activeCampaignDetail = action.payload;
        }
        showSuccessToast("Campaign broadcast launched successfully!");
      }
    })
    .addCase(launchWhatsAppCampaign.rejected, (state, action) => {
      state.launchingCampaignId = null;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to launch campaign.";
      showErrorToast(msg);
    })

    // ── Studio Albums & Redeem Audience ─────────────────────────────
    .addCase(getStudioAlbums.pending, (state) => {
      state.studioAlbumsLoading = true;
    })
    .addCase(getStudioAlbums.fulfilled, (state, action) => {
      state.studioAlbumsLoading = false;
      state.studioAlbums = action.payload ?? [];
    })
    .addCase(getStudioAlbums.rejected, (state, action) => {
      state.studioAlbumsLoading = false;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to load studio albums.";
      state.error = { ...state.error, studioAlbums: msg };
    })

    .addCase(getRedeemUsers.pending, (state) => {
      state.redeemUsersLoading = true;
    })
    .addCase(getRedeemUsers.fulfilled, (state, action) => {
      state.redeemUsersLoading = false;
      state.redeemUsers = action.payload ?? [];
    })
    .addCase(getRedeemUsers.rejected, (state, action) => {
      state.redeemUsersLoading = false;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to load redeem customers.";
      state.error = { ...state.error, redeemUsers: msg };
    })

    .addCase(getCampaignAudience.pending, (state) => {
      state.campaignAudienceLoading = true;
    })
    .addCase(getCampaignAudience.fulfilled, (state, action) => {
      state.campaignAudienceLoading = false;
      state.campaignAudience = action.payload ?? null;
    })
    .addCase(getCampaignAudience.rejected, (state, action) => {
      state.campaignAudienceLoading = false;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to load audience preview.";
      state.error = { ...state.error, campaignAudience: msg };
    })

    .addCase(getCampaignWalletEstimate.pending, (state) => {
      state.walletEstimateLoading = true;
    })
    .addCase(getCampaignWalletEstimate.fulfilled, (state, action) => {
      state.walletEstimateLoading = false;
      state.walletEstimate = action.payload ?? null;
      if (action.payload?.wallet_balance_inr && state.walletBalance?.data) {
        state.walletBalance.data.balance = action.payload.wallet_balance_inr;
      }
    })
    .addCase(getCampaignWalletEstimate.rejected, (state, action) => {
      state.walletEstimateLoading = false;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to estimate campaign wallet cost.";
      state.error = { ...state.error, walletEstimate: msg };
    })

    // ── Templates ───────────────────────────────────────────────────
    .addCase(getWhatsAppTemplates.pending, (state) => {
      state.loading = true;
      state.error = { ...state.error, templates: "" };
    })
    .addCase(getWhatsAppTemplates.fulfilled, (state, action) => {
      state.loading = false;
      state.error = { ...state.error, templates: "" };
      state.templates = action.payload ?? [];
    })
    .addCase(getWhatsAppTemplates.rejected, (state, action) => {
      state.loading = false;
      state.isError = true;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to load templates.";
      state.error = { ...state.error, templates: msg };
      showErrorToast(msg);
    })

    .addCase(getWhatsAppTemplateDetail.fulfilled, (state, action) => {
      if (action.payload) {
        const idx = state.templates.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1) {
          state.templates[idx] = action.payload;
        } else {
          state.templates.push(action.payload);
        }
      }
    })

    .addCase(createWhatsAppTemplate.pending, (state) => {
      state.loading = true;
    })
    .addCase(createWhatsAppTemplate.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.templates = [action.payload, ...state.templates];
      }
      showSuccessToast("Template submitted for Meta approval. Please sync status once reviewed.");
    })
    .addCase(createWhatsAppTemplate.rejected, (state, action) => {
      state.loading = false;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to create template.";
      showErrorToast(msg);
    })

    .addCase(syncWhatsAppTemplate.pending, (state, action) => {
      state.syncingTemplateId = action.meta.arg;
    })
    .addCase(syncWhatsAppTemplate.fulfilled, (state, action) => {
      state.syncingTemplateId = null;
      if (action.payload) {
        const updated = action.payload;
        const idx = state.templates.findIndex((t) => t.id === updated.id);
        if (idx !== -1) {
          state.templates[idx] = { ...state.templates[idx], ...updated };
        }
        const statusLabel = updated.status ? updated.status.toUpperCase() : "SYNCED";
        const catLabel = updated.category ? `(${updated.category})` : "";
        showSuccessToast(`Template updated: Status is ${statusLabel} ${catLabel}`);
      }
    })
    .addCase(syncWhatsAppTemplate.rejected, (state, action) => {
      state.syncingTemplateId = null;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to sync template status from Meta.";
      showErrorToast(msg);
    })

    .addCase(deleteWhatsAppTemplate.pending, (state, action) => {
      state.deletingTemplateId = action.meta.arg.templateId;
    })
    .addCase(deleteWhatsAppTemplate.fulfilled, (state, action) => {
      state.deletingTemplateId = null;
      state.templates = state.templates.filter((t) => t.id !== action.payload);
      showSuccessToast("Template deleted successfully.");
    })
    .addCase(deleteWhatsAppTemplate.rejected, (state, action) => {
      state.deletingTemplateId = null;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to delete template.";
      showErrorToast(msg);
    })

    // ── Wallet Recharge (Cashfree flow) ───────────────────────────────
    .addCase(createRechargeOrder.pending, (state) => {
      state.rechargeLoading = true;
    })
    .addCase(createRechargeOrder.fulfilled, (state, action) => {
      state.rechargeLoading = false;
      state.rechargeOrder = action.payload.data ?? null;
    })
    .addCase(createRechargeOrder.rejected, (state, action) => {
      state.rechargeLoading = false;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to create recharge order.";
      showErrorToast(msg);
    })

    .addCase(verifyRecharge.pending, (state) => {
      state.rechargeVerifying = true;
    })
    .addCase(verifyRecharge.fulfilled, (state, action) => {
      state.rechargeVerifying = false;
      state.rechargeOrder = null;
      if (state.walletBalance?.data && action.payload.data) {
        state.walletBalance.data.balance = action.payload.data.wallet_balance;
      }
      showSuccessToast("Payment verified and recharge credited successfully.");
    })
    .addCase(verifyRecharge.rejected, (state, action) => {
      state.rechargeVerifying = false;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to verify recharge.";
      showErrorToast(msg);
    })

    .addCase(cancelRechargeOrder.pending, (state) => {
      state.rechargeLoading = true;
    })
    .addCase(cancelRechargeOrder.fulfilled, (state) => {
      state.rechargeLoading = false;
      state.rechargeOrder = null;
      showSuccessToast("Recharge order cancelled.");
    })
    .addCase(cancelRechargeOrder.rejected, (state, action) => {
      state.rechargeLoading = false;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to cancel recharge order.";
      showErrorToast(msg);
    })

    .addCase(getRechargeOrders.pending, (state) => {
      state.rechargeOrdersLoading = true;
    })
    .addCase(getRechargeOrders.fulfilled, (state, action) => {
      state.rechargeOrdersLoading = false;
      const data = action.payload.data;
      if (data) {
        if (data.current_page === 1) {
          state.rechargeOrders = data.results ?? [];
        } else {
          state.rechargeOrders = [...state.rechargeOrders, ...(data.results ?? [])];
        }
        state.rechargeOrdersPage = data.current_page;
        state.rechargeOrdersTotalPages = data.total_pages;
      }
    })
    .addCase(getRechargeOrders.rejected, (state, action) => {
      state.rechargeOrdersLoading = false;
      const msg = (action.payload as { message?: string })?.message ?? "Failed to load recharge orders.";
      showErrorToast(msg);
    });
};
